import { useMemo, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIsJobCoach } from '@App/components/AuthProvider/auth-provider.context';
import { normalizeText } from '@services/utils.service';
import {
  Outlet, useOutletContext, useNavigate, useMatch, useParams,
} from 'react-router-dom';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@nazka/nazka.mapframe.components';

import { resetVacancy, setVacancy } from '@App/pages/JobVacancy/job-vacancy.slice';
import RouteInfoControl from '@App/components/Map/controls/RouteInfo/RouteInfo';
import RouteOptionsControl from '@App/components/Map/controls/RouteOptions/RouteOptions';
import { getCutoffDate, vacancyMatchesFilter } from '@App/pages/Filter/filter.utils';
import { useVacancy } from '@services/query.service';
import { useCurrentJobDestination } from './NearbyCompanySection/nearby-company-section.utils';

import NearbyCompanySection from './NearbyCompanySection/NearbyCompanySection';
import CompanyVacancyList from './CompanyVacancyList/CompanyVacancyList';
import CompanyDataGraph from './CompanyDataGraph/CompanyDataGraph';

import './shared-company-info.styl';

function SharedCompanyInfo() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { map } = useOutletContext();
  const homeAddress = useSelector(state => state.map.homeAddress);
  const searchGeometry = useSelector(state => state.map.searchGeometry);
  const breakpoint = useSelector(state => state.windowObserver.breakpoint);
  const { useJobApplicationLocation, OIDN, vdabReferentie } = useSelector(state => state.vacancy);
  const filter = useSelector(state => state.filter);
  const isJobCoach = useIsJobCoach();
  
  const onMobilityOfferPage = useMatch(`/${OIDN}/${vdabReferentie}/mobiliteitsaanbod`);
  const showingMobilityOffer = useMatch(`/${OIDN}`) || onMobilityOfferPage;

  const contentContainer = useRef(null);
  const descriptionContainer = useRef(null);
  const metaContainer = useRef(null);

  const { companyId, vacancyId } = useParams();

  useEffect(() => {
    if (companyId !== OIDN || vacancyId !== vdabReferentie) {
      dispatch(setVacancy({
        OIDN: companyId,
        vdabReferentie: Number(vacancyId),
      }));
    }
  }, [OIDN, companyId, dispatch, vacancyId, vdabReferentie]);

  const {
    data: companyData,
    isLoading,
  } = useVacancy(OIDN);

  const currentJobDestination = useCurrentJobDestination(companyData, vacancyId);

  useEffect(() => {
    if (map && !homeAddress && companyData?.geometry?.coordinates) {
      map.flyTo({
        center: companyData?.geometry?.coordinates,
        zoom: 14,
      });
    }
  }, [map, homeAddress, companyData]);

  useEffect(() => {
    const routeInfoControl = new RouteInfoControl(isJobCoach);
    const routeOptionsControl = new RouteOptionsControl(isJobCoach);

    if (map) {
      map.addControl(routeInfoControl, 'bottom-right');
      if (homeAddress) map.addControl(routeOptionsControl, 'top-right');
    }

    return () => {
      if (map) {
        map.removeControl(routeInfoControl);
        map.removeControl(routeOptionsControl);
      }
    }
  }, [map, isJobCoach, homeAddress]);

  useEffect(() => {
    if (map && companyData) {
      map.getSource('vacancy')?.setData(currentJobDestination ? {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [currentJobDestination.lng, currentJobDestination.lat],
        },
        properties: {
          isJobAggregator: companyData.properties?.isJobAggregator,
          hasHouseNb: companyData.properties?.CRAB_HNR,
        },
      } : { type: 'FeatureCollection', features: [] });
    }
  }, [map, homeAddress, currentJobDestination, companyData]);

  useEffect(() => {
    if (map) {
      map.setLayoutProperty('vacancies', 'visibility', 'none');
      map.setLayoutProperty('vacancies-clusters', 'visibility', 'none');
    }

    return () => {
      dispatch(resetVacancy());

      if (map) {
        map.setLayoutProperty('vacancies', 'visibility', 'visible');
        map.setLayoutProperty('vacancies-clusters', 'visibility', 'visible');
      }
    }
  }, [map, dispatch]);

  useEffect(() => {
    if (map) {
      if (useJobApplicationLocation) {
        map.setLayoutProperty('vacancy', 'visibility', 'none');
        map.setLayoutProperty('sollicitation', 'visibility', 'visible');
      } else {
        map.setLayoutProperty('vacancy', 'visibility', 'visible');
        map.setLayoutProperty('sollicitation', 'visibility', 'none');
      }
    }

    return () => {
      if (map) {
        map.setLayoutProperty('vacancy', 'visibility', 'none');
        map.setLayoutProperty('sollicitation', 'visibility', 'none');
      }
    }
  }, [map, useJobApplicationLocation]);

  const vacancyData = useMemo(() => {
    return companyData?.properties?.vacancies.find((v) => {
      return v?.properties?.vacatureReferentie?.vdabReferentie === vdabReferentie;
    })
  }, [companyData, vdabReferentie]);

  const { vacatureReferentie } = vacancyData?.properties || {};
  const {
    OND_VESTNR, companyActivities, vacancies, NAZKA_NM,
  } = companyData?.properties || {};

  const otherVacancies = vacancies?.filter((v) => {
    return (v?.properties?.vacatureReferentie?.interneReferentie
      !== vacancyData?.properties?.vacatureReferentie?.interneReferentie)
  }) || [];

  const showMobilityOfferButton = !showingMobilityOffer && vacancyId;
  
  const onVacancyClick = (vacancy) => {
    dispatch(setVacancy({
      vdabReferentie: vacancy.properties?.vacatureReferentie?.vdabReferentie,
    }));
    contentContainer.current.scrollTop = 0;
    descriptionContainer.current.scrollTop = 0;
    metaContainer.current.scrollTop = 0;
    navigate(`/${OIDN}/${vacancy.properties?.vacatureReferentie?.vdabReferentie}`);
  }

  let moreVacaturesTitle = vdabReferentie ? 'Andere vacatures bij deze werkgever die voldoen aan je zoekactie' : 'Vacatures bij deze werkgever die voldoen aan je zoekactie';
  if (!searchGeometry) moreVacaturesTitle = 'Vacatures bij deze werkgever';

  return (
    <div className={`shared-company-info${isLoading ? ' is-loading' : ''}`}>
      {onMobilityOfferPage && vacancyData && breakpoint?.maxWidth <= 767 && (
        <Button
          icon={faArrowLeft}
          className="shared-company-info__content__top__meta__button-back-top"
          layout="ghost"
          hover={false}
          onClick={() => navigate(`/${OIDN}/${vdabReferentie}`)}
        >
          Terug naar vacature
        </Button>
      )}
      <div className="shared-company-info__header">
        <h2 className="shared-company-info__header__title">
          {normalizeText(NAZKA_NM)}
        </h2>
        {isJobCoach && (
          <p className="shared-company-info__header__subtitle">
            {`${companyActivities ? companyActivities.map(a => normalizeText(a?.Description)).join(', ') : ''}`}
            {`${!OND_VESTNR?.includes('NZKA-') ? ` | Vestiging: ${OND_VESTNR}` : ''}`}
          </p>
        )}
      </div>
      <div
        className="shared-company-info__content"
        ref={contentContainer}
      >
        {showMobilityOfferButton && (
          <Button
            icon="/img/icons/mobiliteitsaanbod.svg"
            className="shared-company-info__content__top__mobiliteitsaanbod"
            layout="ghost"
            hover={false}
            onClick={() => {
              navigate(`/${OIDN}/${vdabReferentie}/mobiliteitsaanbod`);
              window.gtag?.('event', 'vacancy_click_mobiliteitsaanbod_button', {
                event_category: 'Vacancy',
                event_label: 'Vacancy click "mobiliteitsaanbod" button',
                vdabReferentie,
                OIDN,
              });
            }}
          >
            Mobiliteitsaanbod van de werkgever
          </Button>
        )}
        <div className="shared-company-info__content__top">
          <div
            ref={descriptionContainer}
            className="shared-company-info__content__top__description"
          >
            <Outlet context={{ map }} />
          </div>
          <div
            ref={metaContainer}
            className="shared-company-info__content__top__meta"
          >
            {showMobilityOfferButton && (
              <Button
                icon="/img/icons/mobiliteitsaanbod.svg"
                className="shared-company-info__content__top__meta__button-mobiliteitsaanbod"
                layout="ghost"
                hover={false}
                onClick={() => {
                  navigate(`/${OIDN}/${vdabReferentie}/mobiliteitsaanbod`);
                  window.gtag?.('event', 'vacancy_click_mobiliteitsaanbod_button', {
                    event_category: 'Vacancy',
                    event_label: 'Vacancy click "mobiliteitsaanbod" button',
                    vdabReferentie,
                    OIDN,
                  });
                }}
              >
                Mobiliteitsaanbod van de werkgever
              </Button>
            )}
            {onMobilityOfferPage && vacancyData && (
              <Button
                icon={faArrowLeft}
                className="shared-company-info__content__top__meta__button-back"
                layout="ghost"
                hover={false}
                onClick={() => navigate(`/${OIDN}/${vdabReferentie}`)}
              >
                Terug naar vacature
              </Button>
            )}
            {vacancyData && vacancyId && (
              <Button
                icon="/img/logo/vdab.svg"
                className="shared-company-info__content__top__meta__button-vdab"
                layout="ghost"
                hover={false}
                onClick={() => {
                  window.open(
                    `https://www.vdab.be/vindeenjob/vacatures/${vacatureReferentie?.vdabReferentie}`,
                    '_blank',
                  );
                  window.gtag?.('event', 'vacancy_click_vdab_more_info_button', {
                    event_category: 'Vacancy',
                    event_label: 'Vacancy click VDAB more info button',
                    vdabReferentie: vacatureReferentie?.vdabReferentie,
                  });
                }}
              >
                Meer informatie
              </Button>
            )}
            <NearbyCompanySection currentJobDestination={currentJobDestination} map={map} />
            {vacancies && !vacancies.length && (
              <h4 className="shared-company-info__content__top__meta__other-vacancies__title">
                Momenteel geen vacatures bij deze werkgever
              </h4>
            )}
            {otherVacancies && !!otherVacancies.length && (
              <>
                <CompanyVacancyList
                  title={moreVacaturesTitle}
                  vacancies={searchGeometry ? otherVacancies?.filter(vacancy => vacancyMatchesFilter(vacancy, filter, getCutoffDate(filter.daysSinceReleaseLimit))) : otherVacancies}
                  onVacancyClick={onVacancyClick}
                />
                {searchGeometry && (
                  <CompanyVacancyList
                    title="Nog meer vacatures bij deze werkgever"
                    vacancies={otherVacancies?.filter(vacancy => !vacancyMatchesFilter(vacancy, filter, getCutoffDate(filter.daysSinceReleaseLimit)))}
                    onVacancyClick={onVacancyClick}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {showingMobilityOffer && (
          <div className="shared-company-info__content__bottom">
            <CompanyDataGraph />
          </div>
        )}
      </div>
    </div>
  )
}

export default SharedCompanyInfo;
